import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Inline validation banner for expected rejections — a release date clashing with the first
 * appointment, and the like.
 *
 * Inline rather than the app's alert modal, for two reasons. The design says the blocking condition
 * is "written out, not implied" (screen 5a annotation 4) and puts the equivalent release-date
 * conflict beside the field on screen 8b. And the alert is the catch-all for unexpected failures —
 * an email that already exists, a request that fell over — so routing an ordinary validation
 * outcome through it makes the coordinator dismiss a dialog before they can see what to change.
 *
 * Two severities, because the frames draw two different things:
 *
 * - `warning` (default) — 8a's incomplete-enrolment notice. Nothing is wrong yet; the coordinator
 *   simply has not finished. Warning-ink on warning-light. The live build's orange-on-orange fails
 *   4.5:1, so those values are not reused.
 * - `error` — 8b's release-date conflict. A save was refused and a value already on screen is now
 *   invalid, so it is drawn in red and paired with the field's own error state.
 */

interface Props {
  title: string;
  detail?: string;
  severity?: "warning" | "error";
}

const ValidationBanner: React.FC<Props> = ({
  title,
  detail,
  severity = "warning",
}) => (
  <Box
    role="alert"
    sx={{
      // Error takes theme tokens (#FCD6D0 on #AB0707) plus an outline, so it reads as a refusal
      // rather than a tint. Warning keeps the hand-off literals it shipped with.
      backgroundColor: severity === "error" ? "error.light" : "#FFE7C2",
      color: severity === "error" ? "error.dark" : "#7A3200",
      border: severity === "error" ? "1px solid" : undefined,
      borderColor: severity === "error" ? "error.main" : undefined,
      borderRadius: 1,
      p: 2,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {detail ? <Typography variant="body2">{detail}</Typography> : null}
  </Box>
);

export default ValidationBanner;
